import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import useHistory from 'app/hooks/history';

// "Save your page(s) before moving on?" guard. By design this covers only
// the two ways a member can actually LEAVE the book-creation page/tab —
// switching pages, switching book-creation steps, and other in-app
// navigation are intentionally NOT guarded (`guard` is only used internally,
// below):
//  - A real route change (Back/Forward, a link elsewhere): the returned
//    <Prompt> element (render it once, anywhere under the Router) uses the
//    standard block-then-replay approach — `guard` blocks the first attempt
//    and opens the modal; confirming re-issues the same navigation via the
//    deferred action.
//  - Closing/refreshing the tab: covered separately via `beforeunload`,
//    since custom UI can't reliably be shown during browser unload.
export default function useUnsavedChangesGuard({ hasUnsavedChanges, onSave, onDiscard }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const pendingActionRef = useRef(null);
  const { push } = useHistory();

  const openWithAction = useCallback(action => {
    pendingActionRef.current = action;
    setError(null);
    setIsOpen(true);
  }, []);

  const guard = useCallback(action => {
    if (!hasUnsavedChanges) {
      action();
      return;
    }
    openWithAction(action);
  }, [hasUnsavedChanges, openWithAction]);

  const runPendingAction = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsOpen(false);
    if (!action) return;
    // Deferred a tick: `action` may be a <Prompt> replay (`push(...)`), which
    // must not run until `hasUnsavedChanges` has actually re-rendered to
    // false — otherwise the still-truthy `when` on the old render blocks its
    // own replay and the member can never leave.
    setTimeout(action, 0);
  }, []);

  const confirmSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const ok = await onSave();
      if (!ok) {
        setError("We couldn't save your changes. Please try again.");
        return;
      }
      runPendingAction();
    } finally {
      setIsSaving(false);
    }
  }, [onSave, runPendingAction]);

  const confirmDiscard = useCallback(() => {
    onDiscard();
    runPendingAction();
  }, [onDiscard, runPendingAction]);

  const cancel = useCallback(() => {
    pendingActionRef.current = null;
    setError(null);
    setIsOpen(false);
  }, []);

  // Real route changes (Back/Forward button, a link outside this guard's
  // reach). Blocks the first attempt and replays it via the same `guard`
  // machinery once the member confirms.
  const handleBlockedNavigation = useCallback(nextLocation => {
    guard(() => push(`${nextLocation.pathname}${nextLocation.search}`));
    return false;
  }, [guard, push]);

  // Tab close / refresh — native confirmation only; a custom modal can't be
  // guaranteed to show during unload.
  useEffect(() => {
    const handler = event => {
      if (!hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const promptElement = (
    <Prompt when={hasUnsavedChanges} message={handleBlockedNavigation} />
  );

  return {
    guard,
    isOpen,
    isSaving,
    error,
    confirmSave,
    confirmDiscard,
    cancel,
    promptElement,
  };
}
